import classNames from "classnames";
import { forwardRef, useId, useRef } from "react";
import { InputAttributes, NumberFormatValues, NumericFormat, NumericFormatProps } from "react-number-format";
import useI18n from "../../hooks/useTranslations";
import SvgArrowDropDown from "../icons/ArrowDropDown";
import SvgArrowDropUp from "../icons/ArrowDropUp";
import Icon from "./Icon";
import inputStyles from "./Input.module.css";
import styles from "./Numeric.module.css";

export type Props = Omit<NumericFormatProps<InputAttributes>, "onChange" | "onValueChange" | "onBlur" | "max"> & {
  isInvalid?: boolean;
  controls?: {
    scale: number;
    onUpdate: (value: number) => void;
  };
  min?: number;
  width?: `${number}px`;
  onBlur?: (value: number | null) => void;
};

const powerOfDecimals = (numStr: string): number => {
  const d = numStr.split(".")[1];
  return d ? Math.pow(10, d.length) : 1;
};

const ALLOWED_CONTROL_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Backspace",
  "Delete",
  "End",
  "Enter",
  "Escape",
  "Home",
  "Tab",
]);

const normalizeNumericValue = (value: Props["value"]): number | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const nextValue = typeof value === "number" ? value : Number(value);
  return Number.isNaN(nextValue) ? null : nextValue;
};

const Numeric = forwardRef<HTMLInputElement, Props>(({ isInvalid, controls, min, width, ...rest }, ref) => {
  const { autoComplete = "off", spellCheck = "false", id = useId() } = rest;
  const i18n = useI18n();
  const allowDecimalSeparator = rest.decimalScale !== 0;
  const isAllowedPrintableKey = (key: string) => {
    if (/^\d$/.test(key)) {
      return true;
    }

    if (allowDecimalSeparator && (key === "," || key === ".")) {
      return true;
    }

    if (rest.allowNegative && key === "-") {
      return true;
    }

    return false;
  };

  const click = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, value: number) => {
    e.preventDefault();
    const currentValue = normalizeNumericValue(rest.value) ?? 0;
    const factor = powerOfDecimals(`${rest.value}`) * 10;
    const newValue = Math.round((currentValue + value) * factor) / factor;
    (min === undefined || newValue >= min) && !rest.disabled && controls?.onUpdate(newValue);
  };

  const lastChange = useRef<NumberFormatValues | undefined>(undefined);
  const changed = useRef(false);

  return (
    <div className={styles.numeric}>
      <NumericFormat
        {...rest}
        id={id}
        value={rest.value === undefined || rest.value === null ? "" : rest.value}
        disabled={rest.disabled}
        getInputRef={ref}
        thousandSeparator="."
        decimalSeparator=","
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        onValueChange={(value) => {
          changed.current = true;
          lastChange.current = value;
        }}
        onBlur={() => {
          const value = changed.current ? (lastChange.current?.floatValue ?? null) : normalizeNumericValue(rest.value);

          changed.current = false;
          lastChange.current = undefined;
          rest.onBlur?.(value);
        }}
        onChange={() => {
          // Needed in order to return a number instead of a string.
        }}
        style={{ width: width }}
        className={classNames(inputStyles.input, {
          [inputStyles.invalid]: isInvalid,
        })}
        onKeyDown={(e) => {
          if (e.ctrlKey || e.metaKey || e.altKey || ALLOWED_CONTROL_KEYS.has(e.key)) {
            if (e.key === "Delete") {
              e.stopPropagation();
            }
            return rest.onKeyDown?.(e);
          }

          if (e.key.length === 1 && !isAllowedPrintableKey(e.key)) {
            e.preventDefault();
            return;
          }

          if (e.key === "Delete") {
            e.stopPropagation();
          }
          return rest.onKeyDown?.(e);
        }}
      />
      {controls && (
        <div className={classNames(styles.controls, { [styles.disabled]: rest.disabled })}>
          <button onClick={(e) => click(e, controls.scale)} title={i18n.translation.common.increase(controls.scale)}>
            <Icon glyph={SvgArrowDropUp} />
          </button>
          <button onClick={(e) => click(e, -controls.scale)} title={i18n.translation.common.decrease(controls.scale)}>
            <Icon glyph={SvgArrowDropDown} />
          </button>
        </div>
      )}
    </div>
  );
});

export default Numeric;
